import {
  IconButton as MuiIconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  Box,
} from '@mui/material';
import {
  Download,
  Settings,
  QuestionMark,
  Leaderboard,
  MoreVert,
} from '@mui/icons-material';
import React, { useState, useContext, useEffect } from 'react';
import { useTheme } from '@mui/system';
import { GameStateContext } from '../../Providers/GameStates/GameState.jsx';

function DefaultButtons({
  isStatsOpen,
  setStatsOpen,
  isRulesOpen,
  setRulesOpen,
  isSettingsOpen,
  setSettingsOpen,
}) {
  const [anchorEl, setAnchorEl] = useState(null); // For popup menu
  const [installPrompt, setInstallPrompt] = useState(null);
  const { state, dispatch } = useContext(GameStateContext);
  const { isInitialized, hasSeenRules, won } = state;

  const theme = useTheme();
  const isNarrowScreen = useMediaQuery('(max-width: 1100px)');
  const iconButtonSx = {
    mx: 0.25,
    bgcolor: 'transparent',
    '&:hover': { bgcolor: theme.palette.action.hover },
    padding: { xs: '7px', sm: '9px' },
  };

  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    if (!hasSeenRules) {
      setRulesOpen(true);
      dispatch({ type: 'SET_HAS_SEEN_RULES', payload: true });
    }
    if (won) {
      setStatsOpen(true);
    }
  }, [hasSeenRules, won, isInitialized, dispatch]);

  const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          setInstallPrompt(null);
        }
      });
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderPopupMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      TransitionProps={{
        timeout: {
          appear: 250,
          enter: 250,
          exit: 200,
        },
      }}
      sx={{
        '& .MuiPaper-root': {
          mt: 1,
          minWidth: 180,
          borderRadius: '16px',
          backgroundColor: theme.custom.surfaceElevated,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.custom.border}`,
          boxShadow: '0 18px 45px rgba(15, 23, 42, .18)',
        },
      }}
    >
      {!isAppInstalled && (
        <MenuItem
          onClick={() => {
            handleInstallClick();
            handleMenuClose();
          }}
        >
          <Download fontSize="small" sx={{ marginRight: 1 }} /> Install
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          setStatsOpen(true);
          handleMenuClose();
        }}
      >
        <Leaderboard fontSize="small" sx={{ marginRight: 1 }} /> Stats
      </MenuItem>
      <MenuItem
        onClick={() => {
          setSettingsOpen(true);
          handleMenuClose();
        }}
      >
        <Settings fontSize="small" sx={{ marginRight: 1 }} /> Settings
      </MenuItem>
      <MenuItem
        onClick={() => {
          setRulesOpen(true);
          handleMenuClose();
        }}
      >
        <QuestionMark fontSize="small" sx={{ marginRight: 1 }} /> Rules
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ marginLeft: 'auto', display: 'flex' }}>
      {isNarrowScreen ? (
        <>
          <MuiIconButton
            id="popup-menu-btn"
            aria-label="Open app menu"
            color="inherit"
            onClick={handleMenuOpen}
            sx={{
              ...iconButtonSx,
              backgroundColor: Boolean(anchorEl)
                ? theme.palette.action.selected
                : 'inherit',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <MoreVert />
          </MuiIconButton>
          {renderPopupMenu()}
        </>
      ) : (
        <>
          {!isAppInstalled && (
            <MuiIconButton
              id="install-btn"
              aria-label="Install Chaindle"
              color="inherit"
              onClick={handleInstallClick}
              sx={{
                ...iconButtonSx,
              }}
            >
              <Download fontSize="medium" />
            </MuiIconButton>
          )}
          <MuiIconButton
            id="stats-btn"
            aria-label="Open statistics"
            color="inherit"
            onClick={() => setStatsOpen(true)}
            sx={{
              ...iconButtonSx,
            }}
          >
            <Leaderboard />
          </MuiIconButton>
          <MuiIconButton
            id="settings-btn"
            aria-label="Open settings"
            color="inherit"
            onClick={() => setSettingsOpen(true)}
            sx={{
              ...iconButtonSx,
            }}
          >
            <Settings />
          </MuiIconButton>
          <MuiIconButton
            id="rules-btn"
            aria-label="Open rules"
            color="inherit"
            onClick={() => setRulesOpen(true)}
            sx={{
              ...iconButtonSx,
            }}
          >
            <QuestionMark />
          </MuiIconButton>
        </>
      )}
    </Box>
  );
}

export default DefaultButtons;
